import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { withTimeout, processImageUrl } from '../utils/Timeout';

// Card and gallery thumbnails never render wider than ~750 CSS px, even at 2x.
const PRODUCT_IMAGE_WIDTH = 750;

const processProduct = (product) => {
  return {
    ...product,
    imageUrl: processImageUrl(product.imageUrl, PRODUCT_IMAGE_WIDTH),
    image1: processImageUrl(product.image1, PRODUCT_IMAGE_WIDTH),
    image2: processImageUrl(product.image2, PRODUCT_IMAGE_WIDTH),
    image3: processImageUrl(product.image3, PRODUCT_IMAGE_WIDTH),
  };
};

// ✅ FIXED: Better error handling
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await withTimeout(
        axios.get(`${process.env.REACT_APP_API_BASE_URL}rest/public/products`),
        10000
      );
      return response.data.map(product => processProduct(product));
    } catch (error) {
      console.error('Failed to fetch products:', error);
      // Return error message to Redux
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ✅ FIXED: Better error handling
export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await withTimeout(
        axios.get(`${process.env.REACT_APP_API_BASE_URL}rest/public/products/${id}`),
        10000
      );
      return processProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  products: [],
  selectedProduct: null,
  loading: false,
  error: null,
  status: 'idle', // ✅ ADD: Track fetch status
};

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    },
    // ✅ ADD: Reset error state
    clearError: (state) => {
      state.error = null;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.products = action.payload;
        state.loading = false;
        state.error = null;
        state.status = 'succeeded';
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.status = 'failed';
      })
      
      // Fetch single product
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.selectedProduct = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearSelectedProduct, clearError } = productSlice.actions;
export default productSlice.reducer;
